<template>
	<DataTable class="p-shadow-2"
		:value="locatorValues"
		:rowHover="true"
		:paginator="true"
		:rows="tableListPagination.pageSize"
		:loading="loading"
		paginatorTemplate="FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink RowsPerPageDropdown"
		:rowsPerPageOptions="[25,50,100]"
		:currentPageReportTemplate="'{first} ' + $t('Squeeze.General.To') + ' {last} ' + $t('Squeeze.General.Of') + ' {totalRecords}  '"
		:autoLayout="true"
		columnResizeMode="fit"
		selectionMode="single"
		@row-click="onMarkWord($event)"
		@row-hover="onMarkWord($event)"
		@page="onChangePage"
		:scrollable="true"
		scrollHeight="flex"
	>
		<template #loading>
			{{  $t('Squeeze.General.Loading') }}
		</template>
		<template #empty>
			{{  $t('Squeeze.General.NoEntriesFound', { entryName: $t('Squeeze.General.Values') }) }}
		</template>
		<Column field="value" :header="$t('Squeeze.Locators.Value')" style="min-width: 6rem;">
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="slotProps.data.value">
					{{slotProps.data.value}}
				</div>
			</template>
		</Column>
		<Column field="boundingBox.page" :header="$t('Squeeze.Locators.Site')" headerClass="header-center" style="min-width: 4rem; max-width: 6rem; text-align: center;">
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="slotProps.data.boundingBox.page">
					{{slotProps.data.boundingBox.page}}
				</div>
			</template>
		</Column>
		<Column field="boundingBox.x0" header="x0" style="min-width: 4rem;">
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="slotProps.data.boundingBox.x0">
					{{slotProps.data.boundingBox.x0}}
				</div>
			</template>
		</Column>
		<Column field="boundingBox.y0" header="y0" style="min-width: 4rem;">
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="slotProps.data.boundingBox.y0">
					{{slotProps.data.boundingBox.y0}}
				</div>
			</template>
		</Column>
		<Column field="boundingBox.x1" header="x1" style="min-width: 4rem;">
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="slotProps.data.boundingBox.x1">
					{{slotProps.data.boundingBox.x1}}
				</div>
			</template>
		</Column>
		<Column field="boundingBox.y1" header="y1" style="min-width: 4rem;">
			<template #body="slotProps">
				<div class="column-cell" v-tooltip.bottom="slotProps.data.boundingBox.y1">
					{{slotProps.data.boundingBox.y1}}
				</div>
			</template>
		</Column>

		<template #paginatorstart></template>
		<template #paginatorend></template>
	</DataTable>
</template>

<script lang="ts">
import {defineComponent, onMounted, PropType, reactive} from "vue";
import {DocumentFieldValue} from "@dex/squeeze-client-ts";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import {useRoute} from "vue-router";
import useSqueezeStore from "@/apps/squeeze/store";
import {ClientManager} from "@/singletons/ClientManager";
import {TableListSettings, TableSettings} from "@/util/TableSettings";

export default defineComponent({
	name: "DocumentLocatorTestingTable",
	components: { DataTable, Column },
	props: {
		locatorValues: {
			type: Array as PropType<DocumentFieldValue[]>,
		},
		loading: {
			type: Boolean,
			default: false,
		},
	},
	emits: [
		'onMarkWord',
	],
	setup(props, {emit}) {
		const {t} = useI18n();
		const toast = useToast();
		const route = useRoute();

		/** Vuex Store */
		const store = useSqueezeStore();

		/** User API endpoint */
		const userApi = ClientManager.getInstance().squeeze.user;

		/** Current table list pagination */
		const tableListPagination = reactive<TableListSettings>({});

		/**
		 * Click a word to mark
		 * @param event
		 */
		const onMarkWord = (event: any) => {
			emit("onMarkWord", event);
		}

		onMounted(() => {
			tableListPagination.pageSize = TableSettings.getTableListPagination(store, route.name);
		});

		/** Triggered when page is changed */
		const onChangePage = (event: { page: number; rows: number }) => {
			tableListPagination.pageSize = event.rows;
			TableSettings.saveTableListPagination(t, toast, store, userApi, route.name, event.rows);
		}

		return {
			t,
			toast,
			store,
			tableListPagination,
			onMarkWord,
			onChangePage,
		}
	},
});
</script>

<style scoped>

::v-deep(td div.column-cell) {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

::v-deep(.header-center div.p-column-header-content) {
	justify-content: center;
}

</style>


